import { h, Component, Prop, Element, Host } from '@stencil/core';

import { hasSlotContent } from '../../../services/utils';

@Component({
  tag: 'pk-card-media',
  styleUrl: './scss/pk-card-media.scss',
  shadow: true,
})
export class PkCardMedia {
  // Own Properties
  // ====================

  private bodyElem: HTMLElement;
  private bodyMobileElem: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkCardMediaElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** Specify the aspect-ratio of the thumbnail */
  @Prop({ reflect: true }) thumbAspectRatio: '16:9' | '1:1' = '16:9';

  /** Custom height (in pixels) to apply to the thumbnail*/
  @Prop({ reflect: true }) thumbCustomHeight: number;

  /** Custom height (in pixels) to apply to the thumbnail */
  @Prop({ reflect: true }) thumbCustomWidth: number;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.onBodyChange();
    this.onBodyMobileChange();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private get aspectRatio() {
    if (this.thumbCustomHeight && this.thumbCustomWidth) return 'aspect-custom';
    if (this.thumbAspectRatio === '1:1') return 'aspect-square';

    return 'aspect-video';
  }

  private onBodyChange = () => {
    const hasBodyContent = hasSlotContent(this.bodyElem, 'body');
    if (hasBodyContent) return;

    // Align the title to the center and remove bottom div container margin-top
    // if no body content has been provided
    const contentDiv: HTMLDivElement = this.el.shadowRoot.querySelector('.card-media__content');
    contentDiv.style.justifyContent = 'center';

    const bottomDiv: HTMLDivElement = this.el.shadowRoot.querySelector('.card-media__bottom');
    bottomDiv.style.marginTop = '0';
  };

  private onBodyMobileChange = () => {
    const hasBodyMobileContent = hasSlotContent(this.bodyMobileElem, 'body-mobile');
    if (hasBodyMobileContent) return;

    // If no body content for mobile has been provided, clone the body content and append it to the host element with `slot="body-mobile"`.
    // This way developers won't be forced to duplicate the card body content if it remains the same on desktop and mobile.
    const bodySlot: HTMLSlotElement = this.bodyElem.querySelector('slot');
    if (!bodySlot) return;

    const bodyContent = bodySlot.assignedNodes({ flatten: true })[0];
    if (!bodyContent) return;

    const bodyMobileContent = bodyContent.cloneNode(true) as HTMLDivElement;
    bodyMobileContent.setAttribute('slot', 'body-mobile');

    this.el.appendChild(bodyMobileContent);
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.thumbCustomHeight && { '--card-media--thumb-height': `${this.thumbCustomHeight}px` }),
      ...(this.thumbCustomWidth && { '--card-media--thumb-width': `${this.thumbCustomWidth}px` }),
    };

    return (
      <Host style={style}>
        <div class="card-media" part="base">
          <div class="card-media__top flex" part="top">
            <div class={`card-media__thumbnail ${this.aspectRatio}`} part="thumbnail">
              <div class={`card-media__thumbnail--img relative h-0 w-full ${this.aspectRatio}`} part="thumbnail-img">
                <slot name="thumbnail" />
              </div>
            </div>
            <div
              class="card-media__content ml-s flex w-full flex-col justify-center sm:ml-l sm:justify-start"
              part="content"
            >
              <div
                class="card-media__content--title m-0 font-semibold text-m leading-m text-text-01 sm:text-l"
                part="title"
              >
                <slot name="title" />
              </div>
              <div
                class="card-media__content--body m-0 mt-m hidden font-regular text-s text-text-02 sm:block sm:text-m"
                part="body"
                ref={(divElem: HTMLDivElement) => (this.bodyElem = divElem)}
              >
                <slot name="body" onSlotchange={this.onBodyChange} />
              </div>
            </div>
          </div>
          <div class="card-media__bottom m-0 mt-s flex-1 p-0 sm:hidden" part="bottom">
            <div
              class="card-media__content--body m-0 p-0 font-regular text-s text-text-02"
              part="body"
              ref={(divElem: HTMLDivElement) => (this.bodyMobileElem = divElem)}
            >
              <slot name="body-mobile" onSlotchange={this.onBodyMobileChange} />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
